'use client';

import Image from 'next/image';
import { ComponentProps, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { useResponsiveness } from '@/hooks/useResponsiveness';

import { useStoreCard } from './store-card-context';

const logosSize = {
  title: { height: 750, width: 1125 },
  logo: { height: 500, width: 500 },
} as const;

export type TitleImageProps = ComponentProps<'div'>;

export function TitleImage({ className, ...props }: TitleImageProps) {
  const { isMobile } = useResponsiveness();

  const { store } = useStoreCard();

  const titleImageSrc = useMemo(() => {
    const { titleImage } = store;

    if (!titleImage) return '';

    const url = new URL(titleImage.url);

    const height = isMobile
      ? logosSize.title.height / 2
      : logosSize.title.height;
    const width = isMobile ? logosSize.title.width / 2 : logosSize.title.width;

    url.searchParams.set('height', String(height));
    url.searchParams.set('width', String(width));
    url.searchParams.set('mode', 'fit');

    return url.href;
  }, [store, isMobile]);

  const logoSrc = useMemo(() => {
    const { logoImage } = store;

    if (!logoImage) return '';

    const url = new URL(logoImage.url);

    const height = isMobile ? logosSize.logo.height / 2 : logosSize.logo.height;
    const width = isMobile ? logosSize.logo.width / 2 : logosSize.logo.width;

    url.searchParams.set('height', String(height));
    url.searchParams.set('width', String(width));
    url.searchParams.set('mode', 'fit');

    return url.href;
  }, [store, isMobile]);

  return (
    <div
      className={twMerge(
        'relative -mt-8 flex items-center justify-center shadow-lg',
        className,
      )}
      {...props}
    >
      {titleImageSrc && (
        <Image
          className="aspect-[3/2] h-full w-auto rounded-md object-cover"
          src={titleImageSrc}
          alt={`${store.name} title image`}
          width={logosSize.title.width}
          height={logosSize.title.height}
        />
      )}

      {logoSrc && (
        <div className="absolute right-1 bottom-1 aspect-[1/1] w-[30%] min-w-14">
          <Image
            className="rounded-md border-4 border-white object-cover"
            src={logoSrc}
            alt={`${store.name} logo`}
            fill
          />
        </div>
      )}
    </div>
  );
}
